import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useTranslate, useTranslationStatus } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const JeanIIDeBrabant = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const { currentLanguage } = useLanguage();
  
  // Savoir si des traductions sont en cours
  const isTranslating = useTranslationStatus();

  // Traductions des textes statiques
  const { translatedText: titleTranslated } = useTranslate("Jean II de Brabant");
  const { translatedText: subtitleTranslated } = useTranslate("Les Lignages de Bruxelles");
  const { translatedText: descTranslated } = useTranslate("L'histoire de Jean II, Duc de Brabant");
  
  // Traduction du contenu dynamique - SEULEMENT si pageData.content existe
  const { translatedText: contentTranslated } = useTranslate(pageData?.content || "");

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      console.log('🔄 Chargement des données de la page Jean II...');
      const response = await fetch(`${API_BASE}/api/site/pages/jean-ii-brabant`);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Données Jean II chargées:', data);
        setPageData(data);
      }
    } catch (error) {
      console.error('Erreur chargement page:', error);
    } finally {
      setLoading(false);
    }
  };

  // Ne plus afficher de spinner bloquant - afficher la page directement
  return (
    <div className="min-h-screen bg-white">
      {/* Header Image Banner */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
            alt={pageData.title}
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        >
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              {titleTranslated}
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {subtitleTranslated}
            </p>
          </div>
        </div>
      )}

      <div className="py-16" style={{ paddingTop: pageData?.header_image ? '4rem' : '8rem' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Alerte de traduction en cours - SOUS LE HEADER avec z-index élevé */}
          {isTranslating && currentLanguage !== 'fr' && (
            <div 
              className="mb-6 bg-blue-50 border-l-4 border-blue-600 text-blue-800 px-6 py-4 rounded-r-lg shadow-md flex items-center gap-3"
              style={{ fontFamily: '"Lora", serif', position: 'relative', zIndex: 10 }}
            >
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <div>
                <p className="font-semibold">Traduction en cours...</p>
                <p className="text-sm">Le contenu s'affiche en français en attendant la traduction complète.</p>
              </div>
            </div>
          )}
          
          {!pageData?.header_image && (
            <div className="text-center mb-12">
              <h1 
                className="text-5xl font-bold mb-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {titleTranslated}
              </h1>
              <p 
                className="text-gray-600"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {descTranslated}
              </p>
            </div>
          )}

          {/* Affichage du contenu dynamique depuis la DB */}
          {pageData?.content && (
            <div 
              dangerouslySetInnerHTML={{ 
                __html: currentLanguage === 'fr' 
                  ? pageData.content 
                  : (contentTranslated || pageData.content) 
              }}
              style={{ fontFamily: '"Lora", serif' }}
            />
          )}
          
          {/* Si pas de contenu, afficher un message */}
          {!pageData?.content && !loading && (
            <div className="text-center text-gray-500 py-12">
              <p style={{ fontFamily: '"Lora", serif' }}>Aucun contenu disponible pour cette page.</p>
            </div>
          )}

          {/* Contenu statique de secours si pas de contenu en DB */}
          {!pageData?.content && (
          <div className="space-y-8">
            {/* Introduction */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-3xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  TRADUCTION de la Charte de Jean II de Brabant (12 juin 1306)
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-lg max-w-none">
                <p 
                  className="text-gray-600 italic mb-4"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  Reprise de l'article paru dans le bulletin 23 & 23 de notre association en 1965.
                </p>
                <p 
                  className="text-gray-600 italic mb-6"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  Le texte original en néerlandais est extrait de <em>Luyster van Brabant 1ste D. bl 66-69</em>
                </p>
                
                <div 
                  className="bg-amber-50 p-6 rounded-lg border-l-4 mb-6"
                  style={{ borderLeftColor: 'rgb(196, 150, 96)' }}
                >
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Nous, Jean, par la grâce de Notre Seigneur, duc de Lotharingie, de Brabant et de Limbourg, faisons savoir 
                    à tous ceux qui verront et entendront lire cette Lettre, que par suite du litige et du différend qui a 
                    existé entre, d'une part, les gens bien nés issus des sept Lignages de notre ville de Bruxelles et qui 
                    en font partie, à savoir le lignage des enfants de sire Hugues, le lignage Sweerts, le lignage Sleeus, 
                    ceux de Rodenbeke, le lignage Serroelofs, ceux de Uten Steenweghe et ceux du Coudenbergh, et ceux qui 
                    appartiennent à ces sept lignages, et d'autre part, le Commun de la même Ville...
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Articles de la Charte */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-3xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Articles de la Charte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Article 1 */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    1. Responsabilité de la Commune
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    En conséquence, nous disons que nous rendons la Commune responsable des méfaits, forfaits et injustices 
                    que le Commun a commis avec la Commune contre nous et nos lignagers de notre ville de Bruxelles.
                  </p>
                </div>

                {/* Article 2 */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    2. Abolition de la Commune
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Ensuite nous disons qu'il n'y aura plus jamais de Commune dans notre dite ville de Bruxelles.
                  </p>
                </div>

                {/* Article 3 */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    3. Rétablissement des lignages
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Ensuite nous disons que nous rétablissons nos susdites gens des sept lignages ci-dessus dans tous leurs 
                    privilèges et droits et entièrement dans le même état dans lequel jadis ils avaient été placés eux-mêmes 
                    et leurs ancêtres par notre père, notre grand-père et nos ancêtres plus lointains, afin d'administrer 
                    notre ville précitée avec le concours de notre Amman.
                  </p>
                </div>

                {/* Article 4 */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    4. Élection des échevins
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Ensuite nous disons que les sept échevins de notre ville de Bruxelles, unanimement tous les sept, ou bien à 
                    la majorité, éliront d'année en année, huit jours avant la Saint-Jean d'été, sept échevins pris dans les 
                    sept lignages précités et que, sur leur serment, ils choisiront comme les plus capables et les plus aptes.
                  </p>
                </div>

                {/* Article 5 */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    5. Durée du mandat
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Ensuite nous disons qu'aucun échevin ne demeurera échevin plus d'une année.
                  </p>
                </div>

                {/* Article 6 */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    6. Contrôle des métiers
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Ensuite nous disons qu'aucun Métier ne pourra se réunir ni tenir conseil soit en privé soit en public si 
                    ce n'est avec la permission de l'Amman et des échevins de notre ville de Bruxelles.
                  </p>
                </div>

                {/* Article 11 */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    11. Interdiction aux tisserands
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Ensuite nous disons qu'aucun tisserand ni foulon ne résidera à l'intérieur de la ville de Bruxelles et 
                    n'y passera la nuit. Si un tisserand ou foulon restait dans la ville pour y passer la nuit, qu'il soit 
                    à notre merci corps et biens.
                  </p>
                </div>

                {/* Article final */}
                <div className="border-l-4 pl-6" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
                  <h4 
                    className="font-bold text-lg mb-2"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    14. Scellés et validation
                  </h4>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Ensuite nous disons que puisque nos gens de Bruxelles, c'est-à-dire les gens lignagers de cette ville 
                    d'une part et le Commun d'autre part, veulent observer notre sentence, nous leur disons d'apposer leur 
                    sceau commun à la présente charte, ensemble avec notre sceau et ceux de nos Hommes.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Image du sceau */}
            <div className="text-center mb-8">
              <img 
                src="/images/jean-ii-brabant-seal.jpg" 
                alt="Sceau Jean II de Brabant"
                className="w-full max-w-md mx-auto rounded-lg shadow-lg"
              />
              <p 
                className="text-gray-600 mt-4 text-sm italic"
                style={{ fontFamily: '"Lora", serif' }}
              >
                Sceau de Jean II de Brabant
              </p>
            </div>

            {/* Conclusion de la charte */}
            <Card>
              <CardContent className="bg-gray-50 p-8">
                <div className="text-center space-y-4">
                  <p 
                    className="text-gray-700 italic leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    En foi de quoi, voulant que notre sentence consignée dans la présente charte soit désormais tenue et 
                    observée par Nous et nos descendants, Nous, Jean, duc prénommé, avons appendu notre sceau personnel à 
                    cette charte en témoignage de vérité.
                  </p>
                  <Separator className="my-6" />
                  <p 
                    className="text-gray-700 font-semibold"
                    style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  >
                    Et nous, gens de Bruxelles, parce que notre seigneur le duc a ordonné dans sa sentence que nous appendions 
                    notre sceau commun de la ville de Bruxelles à cette charte avec le sceau, pour ce motif nous avons appendu 
                    notre sceau à cette charte.
                  </p>
                  <Separator className="my-6" />
                  <p 
                    className="font-bold"
                    style={{ 
                      fontFamily: '"Lora", serif', 
                      fontSize: '17px',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    <em>Donné et fait en l'an de N.S., 1306, le dimanche après la fête de saint Barnabé (12 juin 1306).</em>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Separator className="my-8" />

            {/* Note historique */}
            <div className="text-center bg-amber-50 p-8 rounded-lg">
              <p 
                className="text-gray-700 leading-relaxed"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                Cette charte historique de 1306 marque un tournant décisif dans l'histoire de Bruxelles, rétablissant 
                définitivement les privilèges des sept lignages et codifiant leur rôle dans l'administration de la ville 
                pour les siècles à venir.
              </p>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JeanIIDeBrabant;